import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, matchPath } from 'react-router-dom';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';

import Link from 'components/Link';

import styles from './styles.module.scss';

const SidebarNav = ({ items }) => {
  const { pathname, hash } = useLocation();

  /**
   * Checks if the supplied route is a match with the
   * current page. If `exact` it specified it will
   * required the path to exactly match instead of
   * just partially matching. E.g.:
   *
   * exact is true:
   * /example/one    ! matches   /example
   * /example/one      matches   /example/one
   *
   * exact is false:
   * /example/one      matches   /example
   * /example/one      matches   /example/one
   * /example/one    ! matches   /example/two
   *
   * @param  {Object}  route The route object
   * @param  {Boolean} exact Whether or not to use exact matching
   * @return {Boolean}       Whether or not the route matches
   */
  const isRouteMatch = (route, exact = true) => {
    const location = `${pathname}${hash}`;
    return matchPath(location, {
      path: route.path,
      exact,
    }) !== null;
  };

  /**
   * The menu items that the user has directly opened
   * or closed, along with those that are supposed to
   * be open initially.
   */
  const [userOpenedItems, setUserOpenedItems] = useState(items.map((item) => (
    item.defaultOpen
  )));

  /**
   * The menu items that are supposed to be open
   * given the page that the user has landed on.
   */
  const [pageOpenedItems, setPageOpenedItems] = useState(items.map((item) => (
    isRouteMatch(item, false)
  )));

  /**
   * Change the opened lists depending on what page the user
   * navigates to.
   */
  useEffect(() => {
    setPageOpenedItems(items.map((item) => isRouteMatch(item, false)));
  }, [pathname, hash]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Whether or not the menu item should be open
   * @param  {Integer} index The menu item index
   * @return {Boolean}       Whether or not it should be open
   */
  const itemIsOpen = (index) => (
    userOpenedItems[index] || pageOpenedItems[index]
  );

  const renderLevel3List = (level3Items) => (
    <ul className={styles['level-3-list']}>
      {level3Items.map((item, index) => (
        <li
          key={index}
          className={[
            isRouteMatch(item, true) ? styles.active : '',
          ].join(' ')}
        >
          <Link
            to={item.path}
            decoration={false}
            className={styles.link}
          >
            {item.nav.text}
          </Link>
        </li>
      ))}
    </ul>
  );

  const renderLevel2List = (level2Items) => (
    <ul className={styles['level-2-list']}>
      {level2Items.map((item, index) => {
        const hasChildren = item.children && item.children.length > 0;
        return (
          <li
            key={index}
            className={[
              isRouteMatch(item, !hasChildren) ? styles.active : '',
            ].join(' ')}
          >
            <Link
              to={item.path}
              decoration={false}
              className={styles.link}
            >
              {item.nav.text}
            </Link>
            {hasChildren && renderLevel3List(item.children)}
          </li>
        );
      })}
    </ul>
  );

  const renderLevel1List = (level1Items) => (
    <ul className={styles['level-1-list']}>
      {level1Items.map((item, index) => {
        const hasChildren = item.children && item.children.length > 0;
        const inner = (
          <>
            <span>{item.nav.icon}</span>
            <span>{item.nav.text}</span>
            {hasChildren && (
              <span>
                {itemIsOpen(index)
                  ? <FiChevronUp />
                  : <FiChevronDown />}
              </span>
            )}
          </>
        );

        return (
          <li
            key={index}
            className={[
              styles.accordion,
              itemIsOpen(index) ? styles.open : '',
              isRouteMatch(item, !hasChildren) ? styles.active : '',
            ].join(' ')}
          >
            {hasChildren
              ? (
                <button
                  type="submit"
                  onClick={() => {
                    setUserOpenedItems([
                      ...userOpenedItems.slice(0, index),
                      !userOpenedItems[index],
                      ...userOpenedItems.slice(index + 1),
                    ]);
                  }}
                >
                  {inner}
                </button>
              )
              : (
                <Link
                  to={item.path}
                  decoration={false}
                  className={styles.link}
                >
                  {inner}
                </Link>
              )}
            {hasChildren && renderLevel2List(item.children)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={styles.nav}>
      {renderLevel1List(items)}
    </div>
  );
};

SidebarNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    path: PropTypes.string,
    nav: PropTypes.shape({
      text: PropTypes.string.isRequired,
      icon: PropTypes.node,
    }),
    children: PropTypes.array,
    defaultOpen: PropTypes.bool,
  })).isRequired,
};

export default SidebarNav;
